import { SyncAlert } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { SyncManagedByPopover } from '@/user_files/external-storage/components/SyncManagedByPopover'

interface FilePickerConnectivityBannerProps {
  provider: ExternalStorageProvider
  isConnector: boolean
  connectorLabel: string | null
  onReconnect: () => void
  onOpenMatterSettings?: () => void
}

/** Disconnected account status and role-aware recovery action for the file picker. */
export function FilePickerConnectivityBanner({ provider, isConnector, connectorLabel, onReconnect, onOpenMatterSettings }: FilePickerConnectivityBannerProps) {
  const providerLabel = PROVIDER_COMPACT_DISPLAY_NAME[provider]
  const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider]

  let action = (
    <Button type="button" variant="plain" color="secondary" size="xs" className="shrink-0" aria-label={`Reconnect ${providerLabel}`} onClick={onReconnect}>
      Reconnect
    </Button>
  )

  if (!isConnector && onOpenMatterSettings) {
    action = (
      <Button
        type="button"
        variant="plain"
        color="secondary"
        size="xs"
        className="shrink-0"
        aria-label={`Disconnect ${providerLabel} in matter settings`}
        onClick={onOpenMatterSettings}
      >
        Disconnect
      </Button>
    )
  } else if (!isConnector) {
    action = (
      <SyncManagedByPopover connectorLabel={connectorLabel} action="reconnect">
        <Button
          type="button"
          variant="plain"
          color="secondary"
          size="xs"
          className="shrink-0"
          aria-label={`Learn more about the ${providerLabel} disconnection`}
        >
          Learn More
        </Button>
      </SyncManagedByPopover>
    )
  }

  return (
    <div
      className="flex min-h-8 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-red-800"
      role="status"
      aria-label={`${providerLabel} disconnected`}
    >
      <ProviderIcon className="size-4 shrink-0" aria-hidden />
      <SyncAlert className="size-4 shrink-0 text-red-700" aria-hidden />
      <span className="flex-1 text-xs font-medium leading-4">Disconnected</span>
      {action}
    </div>
  )
}
